import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';

import { AuthContextType, AuthPayload, AuthState, Scope } from '@/types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { token: string; authPayload: AuthPayload; code?: string; page?: string } }
  | { type: 'LOGIN_ERROR'; payload: string }
  | { type: 'TOKEN_REFRESHED'; payload: { token: string; authPayload: AuthPayload } }
  | { type: 'CLEAR_ERROR' };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        isAuthenticated: true,
        token: action.payload.token,
        payload: action.payload.authPayload,
        authedCode: action.payload.code ?? null,
        authedPage: action.payload.page ?? null,
        isLoading: false,
        error: null,
      };
    case 'LOGIN_ERROR':
      return {
        isAuthenticated: false,
        token: null,
        payload: null,
        authedCode: null,
        authedPage: null,
        isLoading: false,
        error: action.payload,
      };
    case 'TOKEN_REFRESHED':
      // BES-7770: swap in a freshly minted token (current scopes) without
      // flipping isLoading, so ProtectedRoute doesn't flash. Keep authedCode.
      return { ...state, token: action.payload.token, payload: action.payload.authPayload };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

const initialState: AuthState = {
  isAuthenticated: false,
  token: null,
  payload: null,
  authedCode: null,
  authedPage: null,
  isLoading: false,
  error: null,
};

// JWT utility functions
function decodeJWT(token: string): AuthPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = JSON.parse(atob(parts[1]));

    // Normalize scope to a typed array of known values
    const VALID_SCOPES: Scope[] = ['rental_agreements', 'damage_waivers', 'check_in_forms', 'sequence', 'guidebooks', 'add_ons', 'portal'];
    const isScope = (value: unknown): value is Scope =>
      typeof value === 'string' && (VALID_SCOPES as string[]).includes(value);

    const normalizeScopes = (raw: unknown): Scope[] => {
      if (Array.isArray(raw)) {
        return raw.map(String).filter(isScope) as Scope[];
      }
      if (typeof raw === 'string') {
        // Support space or comma delimited scopes
        return raw.split(/[ ,]+/).filter(Boolean).filter(isScope) as Scope[];
      }
      return [];
    };
    // Check both 'scopes' (new backend format) and 'scope' (legacy format)
    const scopeData = payload.scopes || payload.scope;

    // Backend serializes userId as a string in the JWT (createGuestAccessJWT),
    // so accept both shapes. Callers downstream compare userId numerically.
    const rawUserId = payload.userId;
    const userIdNum =
      typeof rawUserId === 'number'
        ? rawUserId
        : typeof rawUserId === 'string' && rawUserId.length > 0
          ? Number(rawUserId)
          : NaN;
    const userId = Number.isFinite(userIdNum) && userIdNum > 0 ? userIdNum : undefined;

    return {
      reservationId: payload.sub,
      guestId: payload.gid,
      scope: normalizeScopes(scopeData),
      jti: payload.jti,
      exp: payload.exp,
      userId,
    };
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

function isTokenExpired(payload: AuthPayload): boolean {
  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now;
}

// BES-7770: persist the access token across refreshes. After the initial
// /:code/:page login the URL is rewritten to the bare protected route (e.g.
// /add-ons), so the in-memory token is the only proof of access. A refresh
// wiped it and dropped the guest to "Authentication Required". sessionStorage
// (not localStorage) keeps it tab-scoped and clears on tab close, so the token
// doesn't linger on shared devices.
const TOKEN_STORAGE_KEY = 'gp_auth_token';
// BES-7770: the direct-link code that minted the stored token, kept in sync with it.
const CODE_STORAGE_KEY = 'gp_auth_code';
// BES-7770: the page key used to mint the token.
const PAGE_STORAGE_KEY = 'gp_auth_page';

function readPersisted(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function persistSession(token: string, code?: string, page?: string): void {
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    // Keep code + page in lockstep with the token.
    if (code) sessionStorage.setItem(CODE_STORAGE_KEY, code);
    else sessionStorage.removeItem(CODE_STORAGE_KEY);
    if (page) sessionStorage.setItem(PAGE_STORAGE_KEY, page);
    else sessionStorage.removeItem(PAGE_STORAGE_KEY);
  } catch {
    // storage unavailable (private mode / quota): auth still works in-memory for this page load
  }
}

function persistTokenOnly(token: string): void {
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // ignore
  }
}

function clearPersistedSession(): void {
  try {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(CODE_STORAGE_KEY);
    sessionStorage.removeItem(PAGE_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Rehydrate auth state from a persisted token, dropping it if missing/expired.
function loadInitialState(): AuthState {
  const token = readPersisted(TOKEN_STORAGE_KEY);
  if (!token) return initialState;

  const payload = decodeJWT(token);
  if (!payload || isTokenExpired(payload)) {
    clearPersistedSession();
    return initialState;
  }

  return {
    isAuthenticated: true,
    token,
    payload,
    authedCode: readPersisted(CODE_STORAGE_KEY),
    authedPage: readPersisted(PAGE_STORAGE_KEY),
    isLoading: false,
    error: null,
  };
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  console.log('AuthProvider rendering');
  const [state, dispatch] = useReducer(authReducer, initialState, loadInitialState);

  const login = useCallback((token: string, code?: string, page?: string) => {
    dispatch({ type: 'LOGIN_START' });

    const payload = decodeJWT(token);
    if (!payload) {
      clearPersistedSession();
      dispatch({ type: 'LOGIN_ERROR', payload: 'Invalid token format' });
      return;
    }

    if (isTokenExpired(payload)) {
      clearPersistedSession();
      dispatch({ type: 'LOGIN_ERROR', payload: 'Token has expired' });
      return;
    }

    persistSession(token, code, page);
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { token, authPayload: payload, code, page },
    });
  }, []);

  const refreshToken = useCallback((token: string) => {
    const payload = decodeJWT(token);
    if (!payload || isTokenExpired(payload)) return;
    persistTokenOnly(token);
    dispatch({ type: 'TOKEN_REFRESHED', payload: { token, authPayload: payload } });
  }, []);

  const hasScope = useCallback(
    (scope: Scope): boolean => {
      console.log(state.payload?.scope, scope);
      return state.payload?.scope.includes(scope) ?? false;
    },
    [state.payload]
  );

  const isTokenValid = useCallback((): boolean => {
    if (!state.payload) return false;
    return !isTokenExpired(state.payload);
  }, [state.payload]);

  const value: AuthContextType = useMemo(
    () => ({
      ...state,
      login,
      refreshToken,
      hasScope,
      isTokenValid,
    }),
    [state, login, refreshToken, hasScope, isTokenValid]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
